import { useCallback, useEffect, useState } from 'react';

import { api } from '../api.ts';
import { useModosithat } from '../felhasznalo-context.ts';

/**
 * Okmánysablonok. (3.0 / 3.)
 *
 * Az okmányok állandó szövegei — megnevezések, hatósági nyilatkozatok,
 * lábjegyzetek. **A kiállított okmányokon a kiállításkori szöveg marad**,
 * a módosítás csak az ezután készülőkre érvényes.
 */

interface SablonMezo {
  kulcs: string;
  cimke: string;
  szoveg: string;
  hosszu: boolean;
}

interface SablonLap {
  kod: string;
  nev: string;
  verzio: number;
  mezok: SablonMezo[];
}

export function Sablonok({ sajatFejlec = true }: { sajatFejlec?: boolean } = {}) {
  const [lapok, setLapok] = useState<SablonLap[]>([]);
  const [aktiv, setAktiv] = useState('HSO');
  const [piszkozat, setPiszkozat] = useState<Record<string, string>>({});
  const [hiba, setHiba] = useState<string | null>(null);
  const [uzenet, setUzenet] = useState<string | null>(null);
  const modosithat = useModosithat('sablon');

  const betolt = useCallback(async () => {
    try {
      const v = await api.get<{ lapok: SablonLap[] }>('/api/sablon');
      setLapok(v.lapok);
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A sablonokat nem sikerült betölteni.');
    }
  }, []);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  const lap = lapok.find((l) => l.kod === aktiv);

  async function ment(kulcs: string, szoveg: string) {
    try {
      const v = await api.patch<{ lapok: SablonLap[] }>(`/api/sablon/${aktiv}/${kulcs}`, { szoveg });
      setLapok(v.lapok);
      setPiszkozat((p) => {
        const uj = { ...p };
        delete uj[kulcs];
        return uj;
      });
      setUzenet('Mentve. A mostantól kiállított okmányokra ez a szöveg kerül.');
      setHiba(null);
      setTimeout(() => setUzenet(null), 4000);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A mentés nem sikerült.');
    }
  }

  return (
    <>
      <div className="dayhead">
        {sajatFejlec && <span style={{ fontSize: 15, fontWeight: 600 }}>Sablonok</span>}
        {/*
          * Beágyazva a fölötte lévő fülsorba olvadt bele, és a megrendelő
          * nem találta a „HSO hátoldal" lapot. Ezért kap saját feliratot.
          * (2026-09-29)
          */}
        {!sajatFejlec && <span className="kdcimke">Melyik okmány:</span>}
        <div className="vsw">
          {lapok.map((l) => (
            <button key={l.kod} className={aktiv === l.kod ? 'on' : undefined} onClick={() => setAktiv(l.kod)}>
              {l.nev}
            </button>
          ))}
        </div>
        {sajatFejlec && (
          <span className="hint">A kiállított okmányokon a kiállításkori szöveg marad.</span>
        )}
      </div>

      {hiba && <div className="uzenet bad">{hiba}</div>}
      {uzenet && <div className="uzenet ok">{uzenet}</div>}

      <div className="panel" style={{ marginTop: 16 }}>
        {lap && lap.mezok.length === 0 && (
          <div className="c">
            Ezen a sablonon nincs szerkeszthető szöveg — a tételt a sofőr adja meg.
          </div>
        )}

        {lap?.mezok.map((m) => {
          const ertek = piszkozat[m.kulcs] ?? m.szoveg;
          const valtozott = ertek !== m.szoveg;
          return (
            <div key={m.kulcs}>
              <p className="lbl" style={{ marginTop: 12 }}>
                {m.cimke}
              </p>
              {m.hosszu ? (
                <textarea
                  className="sabt"
                  value={ertek}
                  disabled={!modosithat}
                  onChange={(e) => setPiszkozat((p) => ({ ...p, [m.kulcs]: e.target.value }))}
                />
              ) : (
                <input
                  className="sabi"
                  value={ertek}
                  disabled={!modosithat}
                  onChange={(e) => setPiszkozat((p) => ({ ...p, [m.kulcs]: e.target.value }))}
                />
              )}
              {valtozott && modosithat && (
                <div className="btns" style={{ marginTop: 6 }}>
                  <button className="btn sm pri" onClick={() => void ment(m.kulcs, ertek)}>
                    Mentés
                  </button>
                  <button
                    className="btn sm"
                    onClick={() =>
                      setPiszkozat((p) => {
                        const uj = { ...p };
                        delete uj[m.kulcs];
                        return uj;
                      })
                    }
                  >
                    Mégse
                  </button>
                </div>
              )}
            </div>
          );
        })}

        <div className="hint" style={{ marginTop: 14 }}>
          A módosítás csak az ezután kiállított okmányokra érvényes. A már kiállítottakon a
          kiállításkori szöveg marad.
          {lap ? ` Ez a sablon most a ${lap.verzio}. változatánál tart.` : ''}
        </div>
      </div>
    </>
  );
}
